import React, {useState} from 'react';
import {
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';
import {ChevronLeft, EllipsisVertical, SlidersHorizontal, Search, XIcon, Download, Settings} from 'lucide-react';

import {Button} from '../shadcn-components/button';

import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '../shadcn-components/dropdown-menu';
import {Input} from '../Input/Input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../shadcn-components/table';
import {columns} from './columns';
import {DataTableProps} from '../../../app/data/types/tables/table-data';
import {translate} from '../../../app/shared/utils/localization-utils';
import {clsx} from 'clsx';
import {useRef} from 'react';
import {DatePicker} from '../DatePicker/DatePicker';
import {DataTableModal} from './tableModal';

export function DataTable<TData>({
	name,
	hasFullTextSearch,
	columnsData,
	data
}: DataTableProps<TData>) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
		[]
	);
	const [inputValue, setInputValue] = useState('');
	const [globalFilter, setGlobalFilter] = useState('');
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
		const saved = localStorage.getItem(`columnVisibility-${name}`);
		if (saved) {
			return JSON.parse(saved);
		}
		return {};
	});
	const [pendingColumnVisibility, setPendingColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [numberOfRecords, setNumberOfRecords] = useState(2);
	const inputRef = useRef<HTMLInputElement>(null);
	const [myDate, setMyDate] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isFiltrationOpen, setIsFiltrationOpen] = useState(false);
	const filtrationsRef = useRef<HTMLDivElement>(null);
	const [filtrationHeight, setFiltrationHeight] = useState<number>(0);

	const customColumns = columns(columnsData);

	const table = useReactTable({
		data,
		columns: customColumns,
		state: {
			globalFilter,
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection
		},
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		initialState: {
			pagination: {
				pageIndex: 0,
				pageSize: numberOfRecords,
			}
		},
		onGlobalFilterChange: setGlobalFilter,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		globalFilterFn: 'includesString',
	});

	const totalRecordsNumber = data.length;
	const currentPageNumber = table.getState().pagination.pageIndex + 1;
	const currentNumberOfRecords = table.getRowModel().rows.length;
	const startCurrentRecord = currentPageNumber === 1 ? currentPageNumber : currentPageNumber * numberOfRecords - numberOfRecords + 1;
	const endCurrentRecord = currentPageNumber === 1 ? numberOfRecords : (currentPageNumber - 1) * numberOfRecords + currentNumberOfRecords;
	const anySelectorTrue = columnsData.some(col => col.isSelector === true);

	const save = () => {
		setColumnVisibility(pendingColumnVisibility);
		table.setColumnVisibility(pendingColumnVisibility);
		localStorage.setItem(`columnVisibility-${name}`, JSON.stringify(pendingColumnVisibility));
		setIsModalOpen(false);
	};
	const close = () => setIsModalOpen(false);

	return (
		<div className="w-full">
			{hasFullTextSearch &&
                <div>
                    <div className="relative flex items-center py-4">
                        <Input
                            text={translate('testingPage.table.fulltext')}
                            name="globalSearch"
                            value={inputValue}
                            className={''}
                            inputClass={'pr-[110px]'}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyPress={e => {
								if (e.key === 'Enter') {
									e.preventDefault();
									setGlobalFilter(inputValue);
								}
							}}

                        />
                        <div className={'absolute right-[20px] top-[calc(50%_-_12px)] flex items-center gap-3'}>
							{globalFilter && <XIcon className={'cursor-pointer w-5 h-5 text-secondary-btn'} onClick={() => {
								setInputValue('');
								setGlobalFilter('');
							}}/>}
                            <Search
                                className={'cursor-pointer size-5 w-5 h-5 text-secondary-btn'}
                                onClick={() => {
									setGlobalFilter(inputValue);
									if (inputRef.current) {
										const len = inputRef.current.value.length;
										inputRef.current.focus();
										inputRef.current.setSelectionRange(len, len); // 👈 kurzor na koniec
									}
								}}
                            />
                            <SlidersHorizontal
                                className={'cursor-pointer size-5 w-5 h-5 text-secondary-btn'}
                                onClick={() => {
									const el = filtrationsRef.current;
									if (el) {
										const rect = el.getBoundingClientRect();
										setFiltrationHeight(rect.height);
									}
									setIsFiltrationOpen(!isFiltrationOpen);
								}}/>
						</div>
                    </div>
                    <div className={`overflow-hidden transform ease-in-out duration-300 `}
                         style={{height: isFiltrationOpen ? `${filtrationHeight}px` : '0px'}}
                    >
                        <div ref={filtrationsRef} className={`overflow-hidden transform ease-in-out duration-300 py-8`}>
                            <DatePicker
                                value={myDate}
                                onChange={(val) => setMyDate(val)}
                                text="Vyber dátum"
                            />
                            <DatePicker
                                value={myDate}
                                onChange={(val) => setMyDate(val)}
                                text="Vyber dátum"
                            />
                        </div>
                    </div>
                </div>
			}
			<div className="rounded-md border">
				<Table className={`overflow-auto table-fixed w-full min-w-[800px] whitespace-nowrap`}>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header, index) => {
									const isFirst = index === 0;
									const isLast = index === headerGroup.headers.length - 1;
									return (
										<TableHead
											key={header.id}
											className={clsx('bg-blue-quarter text-secondary-btn uppercase', {
												'pl-6': isFirst,
												'pr-6': isLast,
											})}
										>
											{header.isPlaceholder
												? null
												: flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) =>
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
								>
									{row.getVisibleCells().map((cell, index, array) => {
										const isFirst = index === 0;
										const isLast = index === array.length - 1;
										return (
											<TableCell key={cell.id}
													   className={clsx('text-secondary-btn', {
														   'pl-6': isFirst,
														   'pr-6': isLast,
													   })}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</TableCell>
										);
									})}
								</TableRow>
							)
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									No results.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div className={`flex items-center py-4 text-[13px] ${anySelectorTrue ? 'justify-between' : 'justify-end'}`}>
				{anySelectorTrue &&
                    <div className="flex items-center gap-1 px-6">
						{translate('selection.numberOfSelectedItems')}:
                        <span>{table.getFilteredSelectedRowModel().rows.length}</span>
                    </div>
				}
				<div className={'flex items-center'}>
					{data.length > numberOfRecords &&
                        <div className={'flex items-center '}>
                            <div className="flex items-center">
								{data.length > numberOfRecords && (
									<div className="flex items-center space-x-2">
								<span className="px-2 text-[13px]">
									{startCurrentRecord} - {endCurrentRecord} {translate('paginator.of')} {totalRecordsNumber}
        						</span>
									</div>
								)}
                            </div>
                            <div className="space-x-2">
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                >
                                    <ChevronLeft className={'stroke-[3] text-secondary-btn'}/>
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                >
                                    <ChevronLeft className={'rotate-180 stroke-[3] text-secondary-btn'}/>
                                </Button>
                            </div>
                        </div>
					}

					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<EllipsisVertical className={`cursor-pointer p`}/>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className={'bg-white p-0'}>
							<DropdownMenuCheckboxItem
								className="capitalize cursor-pointer hover:bg-off-grey px-8"
							>
								<Download className={'text-secondary-btn'}/>
								PDF
							</DropdownMenuCheckboxItem>
							<DropdownMenuCheckboxItem
								className="capitalize cursor-pointer hover:bg-off-grey px-8"
							>
								<Download className={'text-secondary-btn'}/>
								XLS
							</DropdownMenuCheckboxItem>
							<DropdownMenuCheckboxItem
								className="capitalize cursor-pointer hover:bg-off-grey px-8"
								onClick={() => setIsModalOpen(true)}
							>
								<Settings className={'text-secondary-btn'}/>
								{translate('button.settings')}
							</DropdownMenuCheckboxItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
			{isModalOpen && (
				<DataTableModal
					table={table}
					columnsData={columnsData}
					pendingColumnVisibility={pendingColumnVisibility}
					setPendingColumnVisibility={setPendingColumnVisibility}
					save={save}
					close={close}
				/>
			)}
		</div>
	);
}
